import { BuildNode, Change, NodeId, TreeView, StablePlace, StableRange, SharedTree } from '@fluid-experimental/tree';
import { Serializable } from '@fluidframework/datastore-definitions';
import { DestinationPosition, SourcePosition } from '../../..';
import { ChangesAndIds, ElementId, ELEMENTS, OrderedListEditorType } from './types';
import { OrderedListReader } from './orderedListReader';
import { NodeKind } from '../types';
import { logVerbose, logVerboseDecorator } from '../../../log';

export class OrderedListWriter<T> extends OrderedListReader<T> implements OrderedListEditorType<T> {
  constructor(private tree: SharedTree, view: TreeView, root: ElementId) {
    super(view, root);
  }

  /** @inheritdoc */
  public insert(position: DestinationPosition, items: Serializable<T>[]): ChangesAndIds<T> {
    this.validate(position);
    logVerbose('insert', 'inserting items', { position });
    switch (position.place) {
      case 'before':
        return this.insertItems(this.placeBefore(position.elementId), items);
      case 'after':
        return this.insertItems(this.placeAfter(position.elementId), items);
      case 'under':
        return this.insertItems(this.placeUnder(position.elementId), items);
      case 'first':
        return this.insertItems(this.getFirstPlace(position.parentId), items);
      case 'last':
        return this.insertItems(this.getLastPlace(position.parentId), items);
    }
  }

  /** @inheritdoc */
  public move(from: SourcePosition, to: DestinationPosition, destinationRoot?: NodeId): Change[] {
    const item = from.elementId;
    logVerbose('move', 'moving from source position to destination position', { from, to, destinationRoot });

    // move lets in invalid source positions, so we check
    this.validate(from);
    this.validate(to, destinationRoot);

    switch (to.place) {
      case 'before':
        return Change.move(StableRange.only(item), this.placeBefore(to.elementId));
      case 'after':
        return Change.move(StableRange.only(item), this.placeAfter(to.elementId));
      case 'under':
        return Change.move(StableRange.only(item), this.placeUnder(to.elementId));
      case 'first':
        return Change.move(StableRange.only(item), this.getFirstPlace(to.parentId, destinationRoot));
      case 'last':
        return Change.move(StableRange.only(item), this.getLastPlace(to.parentId, destinationRoot));
    }
  }

  /** @inheritdoc */
  public remove(position: ElementId): Change | undefined {
    const exists = this.validateElementId(position, true);
    logVerbose('remove', 'removing element at specified position', { position, exists });
    if (!exists) return undefined;
    return Change.delete(StableRange.only(position));
  }

  /** @inheritdoc */
  public clear(parentId?: ElementId): Change[] {
    return [
      Change.delete(
        StableRange.all({
          parent: parentId ?? this.root,
          label: ELEMENTS,
        })
      ),
    ];
  }

  /** Gets the first place in the list. */
  private getFirstPlace(parentId?: ElementId, destinationPosition?: NodeId): StablePlace {
    const parent = parentId ?? destinationPosition ?? this.root;
    return StablePlace.atStartOf({ parent, label: ELEMENTS });
  }

  /** Gets the last place in the list. */
  private getLastPlace(parentId?: ElementId, destinationPosition?: NodeId): StablePlace {
    const parent = parentId ?? destinationPosition ?? this.root;
    return StablePlace.atEndOf({ parent, label: ELEMENTS });
  }

  /** Gets place before the given position. */
  private placeBefore(position: ElementId): StablePlace {
    return StablePlace.before(this.view.getViewNode(position).identifier);
  }

  /** Gets place after the given position. */
  private placeAfter(position: ElementId): StablePlace {
    return StablePlace.after(this.view.getViewNode(position).identifier);
  }

  /** Gets place for the first child under a given position. */
  private placeUnder(position: ElementId): StablePlace {
    return this.getFirstPlace(this.view.getViewNode(position).identifier);
  }

  /** Inserts multiple items. */
  private insertItems(place: StablePlace, items: Serializable<T>[]): ChangesAndIds<T> {
    const itemNodeIds: [Serializable<T>, NodeId][] = [];
    const newNodes = items.map((item) => {
      const nodeId = this.tree.generateNodeId();
      itemNodeIds.push([item, nodeId]);
      return this.makePositionNode(item, nodeId);
    });
    logVerbose('insertItems', 'item ids currently being inserted', { itemNodeIds, place });
    return {
      changes: Change.insertTree(newNodes, place),
      itemNodeIds,
    };
  }

  /** Creates a list element node. */
  private makePositionNode<T>(value: Serializable<T>, identifier: NodeId): BuildNode {
    return {
      definition: NodeKind.Reference,
      payload: value,
      identifier,
    };
  }

  /** Validate if the `position` refers to a valid snapshot node. */
  private validate(position: SourcePosition | DestinationPosition, root?: NodeId): void {
    if (position.place === 'before' || position.place === 'after' || position.place === 'at') {
      this.validateElementId(position.elementId, false, root);
    }
  }
}
